import { useState, useEffect } from "react";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { PolicySelector } from "@/components/ui/policy-selector";
import { useDepartments } from "@/hooks";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { formatFramework, useCompliancePrograms } from "@/hooks/use-compliance-programs";
import { 
  Flag, 
  Calendar as CalendarIcon, 
  Building2,
  X,
  User,
  Check,
  ShieldCheck
} from "lucide-react";
import { format } from "date-fns";
import { cn } from "@/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import type { ComplianceTask } from "@/hooks/use-compliance-tasks";

interface TeamMember {
  id: string;
  name: string;
  email: string;
  designation: string | null;
}

interface TaskFormModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  task?: ComplianceTask | null;
  defaultTaskData?: Partial<ComplianceTask>;
  onSave: (taskData: Partial<ComplianceTask>) => Promise<void>;
}

export function TaskFormModal({ open, onOpenChange, task, defaultTaskData, onSave }: TaskFormModalProps) {
  const { departments } = useDepartments();
  const { profile } = useAuth();
  const { programs } = useCompliancePrograms();
  const [loading, setLoading] = useState(false);
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [priorityOpen, setPriorityOpen] = useState(false);
  const [departmentOpen, setDepartmentOpen] = useState(false);
  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const [watchersOpen, setWatchersOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const getInitialFormData = (): Partial<ComplianceTask> => ({
    title: '',
    description: '',
    priority: 'p2',
    status: 'not_started',
    department_id: null,
    due_date: null,
    policy_id: null,
    program_id: null,
    program_phase: null,
    reference_type: null,
    primary_assignee_id: null,
    watcher_ids: [],
    ...defaultTaskData,
  });

  const [formData, setFormData] = useState<Partial<ComplianceTask>>(getInitialFormData);

  useEffect(() => {
    if (open && teamMembers.length === 0) {
      fetchTeamMembers();
    }
  }, [open]);

  useEffect(() => {
    if (task) {
      setFormData({
        title: task.title || '',
        description: task.description || '',
        priority: task.priority,
        status: task.status,
        department_id: task.department_id,
        primary_assignee_id: task.primary_assignee_id,
        watcher_ids: task.watcher_ids || [],
        policy_id: task.policy_id || null,
        program_id: task.program_id || null,
        program_phase: task.program_phase || null,
        reference_type: task.reference_type || null,
        due_date: task.due_date ? new Date(task.due_date) : null,
      });
    } else {
      setFormData(getInitialFormData());
    }
  }, [task, open, defaultTaskData]);

  const fetchTeamMembers = async () => {
    if (!profile?.company_id) return;
    
    try {
      const { data: profiles, error } = await supabase
        .from('profiles')
        .select('id, name, email, designation')
        .eq('company_id', profile.company_id);

      if (error) throw error;
      setTeamMembers(profiles || []);
    } catch (error) {
      console.error('Error fetching team members:', error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSave({
        ...formData,
        reference_type: formData.reference_type || (formData.program_id ? 'program' : formData.policy_id ? 'policy' : null),
        due_date: formData.due_date ? format(formData.due_date as Date, 'yyyy-MM-dd') : null,
      });
      onOpenChange(false);
    } catch (error) {
      console.error('Error saving task:', error);
      alert('Failed to save task. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Priority configs
  const priorityConfig = {
    p0: { label: 'Critical', color: 'bg-red-500 hover:bg-red-600 text-white', icon: '🔴' },
    p1: { label: 'High', color: 'bg-orange-500 hover:bg-orange-600 text-white', icon: '🟠' },
    p2: { label: 'Medium', color: 'bg-blue-500 hover:bg-blue-600 text-white', icon: '🔵' },
    p3: { label: 'Low', color: 'bg-gray-500 hover:bg-gray-600 text-white', icon: '⚪' },
  };

  const selectedDepartment = departments?.find(d => d.id === formData.department_id);
  const selectedAssignee = teamMembers.find(m => m.id === formData.primary_assignee_id);
  const selectedWatchers = teamMembers.filter(m => formData.watcher_ids?.includes(m.id));
  const selectedProgram = programs.find((program) => program.id === formData.program_id);

  const formatDueDate = (date: Date | null) => {
    if (!date) return null;
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return { text: 'Today', color: 'bg-green-100 text-green-800 border-green-200' };
    }
    if (date.toDateString() === tomorrow.toDateString()) {
      return { text: 'Tomorrow', color: 'bg-blue-100 text-blue-800 border-blue-200' };
    }
    return { 
      text: format(date, 'MMM d'), 
      color: 'bg-gray-100 text-gray-800 border-gray-200' 
    };
  };

  const dueDateInfo = formatDueDate(formData.due_date as Date | null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 bg-white dark:bg-background text-foreground dark:text-foreground rounded-lg">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          {/* Main Content */}
          <div className="p-5 space-y-2">
            {/* Task Name Input */}
              <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Task name"
              className="!text-2xl font-semibold border-0 px-0 py-4 shadow-none focus-visible:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500"
              style={{ 
                fontSize: '2.25rem !important', 
                lineHeight: '2.5rem !important',
                fontWeight: '600 !important'
              }}
              required
              autoFocus
            />

            {/* Description */}
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description"
              className="resize-none border-0 px-0 py-2 shadow-none focus-visible:ring-0 placeholder:text-gray-400 dark:placeholder:text-gray-500 min-h-[80px]"
              rows={3}
            />

            {/* Action Pills */}
            <div className="flex flex-wrap gap-2 pt-2">
              {/* Priority Pill */}
              <Popover open={priorityOpen} onOpenChange={setPriorityOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 px-3 gap-2 rounded-md border text-sm font-medium cursor-pointer",
                      formData.priority ? priorityConfig[formData.priority as keyof typeof priorityConfig].color : "bg-white hover:bg-gray-50 dark:bg-muted/20 dark:hover:bg-muted/30"
                    )}
                  >
                    <Flag className="h-3 w-3" />
                    {formData.priority ? priorityConfig[formData.priority as keyof typeof priorityConfig].label : 'Priority'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-48 p-1" align="start">
                  <div className="space-y-1">
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <Button
                        key={key}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start gap-2 h-8"
                        onClick={() => {
                          setFormData({ ...formData, priority: key as ComplianceTask['priority'] });
                          setPriorityOpen(false);
                        }}
                      >
                        <span>{config.icon}</span>
                        {config.label}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Due Date Pill */}
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 px-3 gap-2 rounded-md border text-sm font-medium",
                      dueDateInfo ? `${dueDateInfo.color} border` : "bg-white hover:bg-gray-50 dark:bg-muted/20 dark:hover:bg-muted/30"
                    )}
                  >
                    <CalendarIcon className="h-3 w-3" />
                    {dueDateInfo ? dueDateInfo.text : 'Due date'}
                    {formData.due_date && (
                      <X 
                        className="h-3 w-3 ml-1 hover:bg-black/10 rounded-full" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({ ...formData, due_date: null });
                          setDatePickerOpen(false);
                        }}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={formData.due_date as Date | undefined}
                    onSelect={(date) => {
                      setFormData({ ...formData, due_date: date || null });
                      setDatePickerOpen(false);
                    }}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Department Pill */}
              <Popover open={departmentOpen} onOpenChange={setDepartmentOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 px-3 gap-2 rounded-md border text-sm font-medium cursor-pointer",
                      selectedDepartment ? "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-950/30 dark:text-purple-200 dark:border-purple-900" : "bg-white hover:bg-gray-50 dark:bg-muted/20 dark:hover:bg-muted/30"
                    )}
                  >
                    <Building2 className="h-3 w-3" />
                    {selectedDepartment ? selectedDepartment.name : 'Department'}
                    {selectedDepartment && (
                      <X 
                        className="h-3 w-3 ml-1 hover:bg-black/10 rounded-full" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({ ...formData, department_id: null });
                          setDepartmentOpen(false);
                        }}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-1" align="start">
                  <div className="space-y-1">
                    {departments?.map((dept) => (
                      <Button
                        key={dept.id}
                        variant="ghost"
                        size="sm"
                        className="w-full justify-start h-8"
                        onClick={() => {
                          setFormData({ ...formData, department_id: dept.id });
                          setDepartmentOpen(false);
                        }}
                      >
                        <Building2 className="h-3 w-3 mr-2" />
                        {dept.name}
                      </Button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>

              {/* Assignee Pill */}
              <Popover open={assigneeOpen} onOpenChange={setAssigneeOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 px-3 gap-2 rounded-md border text-sm font-medium cursor-pointer",
                      selectedAssignee ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-950/30 dark:text-blue-200 dark:border-blue-900" : "bg-white hover:bg-gray-50 dark:bg-muted/20 dark:hover:bg-muted/30"
                    )}
                  >
                    <User className="h-3 w-3" />
                    {selectedAssignee ? selectedAssignee.name : 'Assignee'}
                    {selectedAssignee && (
                      <X 
                        className="h-3 w-3 ml-1 hover:bg-black/10 rounded-full" 
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({ ...formData, primary_assignee_id: null });
                          setAssigneeOpen(false);
                        }}
                      />
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search team members..." />
                    <CommandList>
                      <CommandEmpty>No team members found.</CommandEmpty>
                      <CommandGroup>
                        {teamMembers.map((member) => (
                          <CommandItem
                            key={member.id}
                            value={`${member.name} ${member.email} ${member.designation || ''}`}
                            onSelect={() => {
                              setFormData({ ...formData, primary_assignee_id: member.id });
                              setAssigneeOpen(false);
                            }}
                          >
                            <Check
                              className={cn(
                                "mr-2 h-4 w-4",
                                formData.primary_assignee_id === member.id ? "opacity-100" : "opacity-0"
                              )}
                            />
                            <div className="flex flex-col">
                              <span className="font-medium">{member.name}</span>
                              <span className="text-sm text-muted-foreground">
                                {member.designation && `${member.designation} • `}{member.email}
                              </span>
                            </div>
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Watchers Pill (Multiple Selection) */}
              <Popover open={watchersOpen} onOpenChange={setWatchersOpen} modal={true}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className={cn(
                      "h-8 px-3 gap-2 rounded-md border text-sm font-medium cursor-pointer",
                      selectedWatchers.length > 0 ? "bg-teal-100 text-teal-800 border-teal-200 dark:bg-teal-950/30 dark:text-teal-200 dark:border-teal-900" : "bg-white hover:bg-gray-50 dark:bg-muted/20 dark:hover:bg-muted/30"
                    )}
                  >
                    <User className="h-3 w-3" />
                    {selectedWatchers.length > 0 
                      ? `${selectedWatchers.length} Watcher${selectedWatchers.length > 1 ? 's' : ''}`
                      : 'Add watchers'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0" align="start">
                  <Command>
                    <CommandInput placeholder="Search team members..." />
                    <CommandList>
                      <CommandEmpty>No team members found.</CommandEmpty>
                      <CommandGroup>
                        {teamMembers
                          .filter(m => m.id !== formData.primary_assignee_id) // Don't show primary assignee in watchers
                          .map((member) => (
                            <CommandItem
                              key={member.id}
                              value={`${member.name} ${member.email} ${member.designation || ''}`}
                              onSelect={() => {
                                const currentWatchers = formData.watcher_ids || [];
                                const isSelected = currentWatchers.includes(member.id);
                                setFormData({ 
                                  ...formData, 
                                  watcher_ids: isSelected 
                                    ? currentWatchers.filter(id => id !== member.id)
                                    : [...currentWatchers, member.id]
                                });
                              }}
                            >
                              <Check
                                className={cn(
                                  "mr-2 h-4 w-4",
                                  formData.watcher_ids?.includes(member.id) ? "opacity-100" : "opacity-0"
                                )}
                              />
                              <div className="flex flex-col">
                                <span className="font-medium">{member.name}</span>
                                <span className="text-sm text-muted-foreground">
                                  {member.designation && `${member.designation} • `}{member.email}
                                </span>
                              </div>
                            </CommandItem>
                          ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              {/* Show selected watchers as badges */}
              {selectedWatchers.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {selectedWatchers.map((watcher) => (
                    <Badge
                      key={watcher.id}
                      variant="outline"
                      className="bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/30 dark:text-teal-200 dark:border-teal-900 px-2 py-1 text-xs flex items-center gap-1"
                    >
                      {watcher.name}
                      <X
                        className="h-3 w-3 cursor-pointer hover:bg-teal-200 rounded-full"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          setFormData({
                            ...formData,
                            watcher_ids: (formData.watcher_ids || []).filter(id => id !== watcher.id)
                          });
                        }}
                      />
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="flex items-center justify-between p-4 border-t bg-gray-50/50 dark:bg-muted/20 dark:border-border">
            <div className="flex flex-1 flex-wrap items-center gap-2">
              <PolicySelector
                value={formData.policy_id}
                onChange={(policyId) => setFormData({ ...formData, policy_id: policyId })}
                placeholder="Link to policy"
                className="max-w-xs"
              />
              <Select
                value={formData.program_id || "none"}
                onValueChange={(programId) => setFormData({
                  ...formData,
                  program_id: programId === "none" ? null : programId,
                  program_phase: programId === "none" ? null : formData.program_phase || "policies",
                  reference_type: programId === "none" ? (formData.policy_id ? "policy" : null) : "program",
                })}
              >
                <SelectTrigger className="h-9 w-[220px] bg-background">
                  <ShieldCheck className="mr-2 h-3.5 w-3.5 text-muted-foreground" />
                  <SelectValue placeholder="Program" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No program</SelectItem>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={program.id}>
                      {formatFramework(program.framework)} · {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedProgram && (
                <Select
                  value={formData.program_phase || "policies"}
                  onValueChange={(phase) => setFormData({ ...formData, program_phase: phase as ComplianceTask["program_phase"] })}
                >
                  <SelectTrigger className="h-9 w-[150px] bg-background">
                    <SelectValue placeholder="Phase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="policies">Policies</SelectItem>
                    <SelectItem value="controls">Controls</SelectItem>
                    <SelectItem value="evidence">Evidence</SelectItem>
                    <SelectItem value="team">Team</SelectItem>
                    <SelectItem value="audit_ready">Audit Ready</SelectItem>
                  </SelectContent>
                </Select>
              )}
            </div>
            
            <div className="flex items-center gap-2">
              <Button 
                type="button" 
                variant="ghost" 
                size="sm"
                onClick={() => onOpenChange(false)}
                className="text-gray-600 hover:text-gray-800"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                disabled={loading || !formData.title.trim()}
                className="px-4"
              >
                {loading ? 'Saving...' : task ? 'Save task' : 'Add task'}
              </Button>
            </div>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
